import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import type { AlwaysAiUser } from "../../../../shared/api-types";
import type { AppointmentListSearch } from "@/lib/appointments";
import {
  parseStoredRailTodayOpen,
  railAppointmentsChevron,
  railAppointmentsMark,
  railAppointmentsNoSelection,
  railTodayOpen,
  railTodayShowsActive,
  railTodayStorageKey,
  railTodayView,
  serializeRailTodayOpen,
} from "@/lib/rail-appointments";
import {
  activeAppointmentIdFor,
  appointmentsTooltipLabel,
  isTodayPanelVisible,
} from "@/lib/siderail-state";
import { usePageVisit } from "@/lib/use-page-visit";
import { useViewedRdName } from "@/lib/use-viewed-rd-name";
import {
  RailAppointmentsRow,
  RailToday,
  type RailAppointmentsLinkProps,
} from "./rail-appointments-parts";
import { AppointmentsLabel } from "./siderail-parts";
import {
  useAppointmentsViewScope,
  useSiderailTodayList,
} from "./siderail-appointments";

// The single rail's Appointments entry (NO-754): the row always links to the
// Appointments list, and its chevron alone shows or hides Today. The open
// state is remembered per user; an open Appointment opens Today by itself.
// Folding the rail hides Today without forgetting it, and Today (with its
// PHI-bearing query) runs only while it is on screen. Shell chrome, so only
// always-rail.tsx imports it.

function readStoredOpen(userId: string): boolean {
  try {
    return parseStoredRailTodayOpen(
      window.localStorage.getItem(railTodayStorageKey(userId))
    );
  } catch {
    return false;
  }
}

function writeStoredOpen(userId: string, open: boolean): void {
  try {
    window.localStorage.setItem(
      railTodayStorageKey(userId),
      serializeRailTodayOpen(open)
    );
  } catch {
    // Storage can be unavailable (private mode); the choice just won't persist.
  }
}

/** Today's open state: the stored choice, the open-Appointment rule, a click. */
function useRailTodayOpen(
  userId: string,
  pathname: string
): { open: boolean; toggle: () => void } {
  const [stored, setStored] = useState(() => readStoredOpen(userId));
  const [clicked, setClicked] = usePageVisit<boolean>(pathname);
  const open = railTodayOpen({ stored, pathname, clickedThisVisit: clicked });

  return {
    open,
    toggle: () => {
      writeStoredOpen(userId, !open);
      setStored(!open);
      setClicked(!open);
    },
  };
}

function renderAppointmentsLink(
  listSearch: AppointmentListSearch
): (props: RailAppointmentsLinkProps) => ReactElement {
  return function AppointmentsLink({
    appointmentId,
    current,
    className,
    ariaLabel,
    children,
  }) {
    const ariaCurrent = current ? "page" : undefined;

    if (appointmentId === null) {
      return (
        <Link
          to="/appointments"
          search={listSearch}
          aria-current={ariaCurrent}
          aria-label={ariaLabel}
          className={className}
        >
          {children}
        </Link>
      );
    }

    return (
      <Link
        to="/appointments/$appointmentId"
        params={{ appointmentId }}
        search={listSearch}
        aria-current={ariaCurrent}
        className={className}
      >
        {children}
      </Link>
    );
  };
}

export function RailAppointmentsEntry({
  user,
  pathname,
  collapsed,
  isDesktop,
  drawerOpen,
}: {
  user: AlwaysAiUser;
  pathname: string;
  collapsed: boolean;
  isDesktop: boolean;
  drawerOpen: boolean;
}): ReactElement {
  const { listSearch, viewingOther } = useAppointmentsViewScope(user);
  const viewedRdName = useViewedRdName(viewingOther ? listSearch.rdId : undefined);
  const today = useRailTodayOpen(user.id, pathname);
  const chevron = railAppointmentsChevron({
    onList: pathname === "/appointments",
    noSelection: railAppointmentsNoSelection(user, listSearch.rdId),
  });
  const visible =
    chevron &&
    isTodayPanelVisible({ scheduleOpen: today.open, collapsed, isDesktop, drawerOpen });
  const list = useSiderailTodayList(listSearch.rdId, visible);
  const view = railTodayView({
    data: list.data?.data,
    isError: list.isError,
    activeAppointmentId: activeAppointmentIdFor(pathname),
  });
  const renderLink = renderAppointmentsLink(listSearch);
  const tooltip = appointmentsTooltipLabel(viewingOther, viewedRdName);

  return (
    <div>
      <RailAppointmentsRow
        label={
          <AppointmentsLabel viewingOther={viewingOther} viewedRdName={viewedRdName} />
        }
        tooltip={tooltip}
        ariaLabel={viewedRdName ? tooltip : undefined}
        mark={railAppointmentsMark({
          pathname,
          todayShowsActive: visible && railTodayShowsActive(view),
        })}
        collapsed={collapsed}
        disclosure={chevron ? { open: today.open, onToggle: today.toggle } : undefined}
        renderLink={renderLink}
      />
      {visible ? <RailToday view={view} renderLink={renderLink} /> : null}
    </div>
  );
}
